import { TimePeriod } from '@/types/TimePeriod'
import { useDeviceManagementStore } from '@nsftx/seven-client-retail-vue-dm'
import { useDmCoreSpaceOperatorStore } from '@nsftx/seven-client-retail-vue-dm-core'
import {
  vouchersApiService,
  type VoucherCurrencyConfiguration,
  type VoucherSearchResponse,
} from '@nsftx/seven-client-retail-vue-sgp'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

export const useVoucherPOSStore = defineStore('voucherPOSStore', () => {
  const PAGE_SIZE = 10
  const TIME_SPAN_HOUR = 60 * 60 * 1000
  const TIME_SPAN_DAY = 24 * 60 * 60 * 1000

  const route = useRoute()
  const page = ref(0)
  const timePeriod = ref<TimePeriod>(TimePeriod.Day)
  const voucherConfig = ref<VoucherCurrencyConfiguration[]>([])
  const voucherSearchData = ref<VoucherSearchResponse>({
    data: [],
    pagination: {
      limit: 0,
      offset: 0,
      page: 0,
      pages: 0,
      size: 0,
      total: 0,
    },
  })
  const isVoucherDetailsModalOpen = ref(false)

  const { appData } = storeToRefs(useDeviceManagementStore())
  const { operatorData } = storeToRefs(useDmCoreSpaceOperatorStore())

  const shouldShowVoucherTable = computed(() => voucherSearchData.value.data.length > 0)

  const showVoucherDetailsModal = () => {
    isVoucherDetailsModalOpen.value = true
  }

  const closeVoucherDetailsModal = () => {
    isVoucherDetailsModalOpen.value = false
  }

  const getMinIssuedAt = () => {
    const span = timePeriod.value === TimePeriod.Hour ? TIME_SPAN_HOUR : TIME_SPAN_DAY
    const date = new Date(Date.now() - span)
    const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000
    const localDate = new Date(date.getTime() - timezoneOffsetMs)
    return localDate.toISOString().slice(0, -1)
  }

  const getVoucherConfig = async () => {
    const res = await vouchersApiService.getVoucherConfig(appData.value!.betshop.companyUuid)
    voucherConfig.value = res.data.channelCurrencyConfiguration?.retail || []
  }

  const searchVouchers = async (page: number) =>
    await vouchersApiService.searchVouchers(appData.value!.betshop.companyUuid, {
      sort: 'issuedAt,desc',
      minIssuedAt: getMinIssuedAt(),
      issuerId: operatorData.value?.uuid,
      size: PAGE_SIZE,
      page,
    })

  const fetchVoucherTableData = async (page: number) => {
    const res = await searchVouchers(page)
    voucherSearchData.value = res.data
  }

  const loadMoreVoucherTableData = async (page: number) => {
    const res = await searchVouchers(page)
    voucherSearchData.value = {
      data: [...voucherSearchData.value.data, ...res.data.data],
      pagination: res.data.pagination,
    }
  }

  watch(
    () => route.fullPath,
    () => {
      closeVoucherDetailsModal()
    },
  )

  return {
    voucherConfig,
    voucherSearchData,
    page,
    timePeriod,
    shouldShowVoucherTable,
    getVoucherConfig,
    fetchVoucherTableData,
    loadMoreVoucherTableData,
    isVoucherDetailsModalOpen,
    showVoucherDetailsModal,
    closeVoucherDetailsModal,
  }
})
